import { Link, type FetcherWithComponents } from "@remix-run/react";
import { Loader2, Star, StarOff } from "lucide-react";
import { Button } from "#app/components/ui/button.tsx";

// Define needed types locally or import from a central types file
type UserId = string; // Assuming string ID, adjust if different
interface Station {
  id: number;
  url: string; // Assuming this is the URL slug/identifier
}

// Define a more specific fetcher type if the data structure returned is known
// For now, using 'any' for simplicity as the data itself isn't used in this component
type FavoriteFetcher = FetcherWithComponents<any>;

interface FavoriteButtonProps {
  stationId: number;
  stationUrl: string; // Passed for constructing the redirect URL
  userId: UserId | undefined; // User might not be logged in
  isFavorite: boolean; // Current favorite status from loader data
  fetcher: FavoriteFetcher; // The fetcher instance for this action
}

export function FavoriteButton({
  stationId,
  stationUrl,
  userId,
  isFavorite, // Use the prop directly reflecting server state
  fetcher,
}: FavoriteButtonProps) {
  // Construct the redirect URL for the login link
  const stationPageUrl = `/estacoes/${stationUrl}/${stationId}`;

  // Common button classes for consistent styling
  const buttonBaseClasses =
    "w-full justify-center rounded-xl font-medium transition-all duration-300 shadow-sm hover:shadow-md hover:translate-y-[-1px] active:translate-y-0";

  return (
    <>
      {userId ? (
        // Logged-in user: Show the form to toggle favorite status
        <fetcher.Form method="post" encType="multipart/form-data">
          <input type="hidden" name="locationId" value={stationId} />
          <input type="hidden" name="userId" value={userId} />
          <input type="hidden" name="intent" value="favorite" />
          <Button
            type="submit"
            variant="ghost"
            className={`${buttonBaseClasses} ${
              isFavorite
                ? "bg-gradient-to-r from-rose-50 to-rose-100 hover:from-rose-100 hover:to-rose-200 dark:from-rose-950/40 dark:to-rose-900/30 dark:text-rose-300"
                : "bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 dark:from-blue-950/40 dark:to-blue-900/30 dark:text-blue-300"
            }`}
            size="lg"
            disabled={fetcher.state !== "idle"}
          >
            {fetcher.state === "idle" ? (
              isFavorite ? (
                <StarOff className="mr-2 h-5 w-5 transition-transform duration-300 ease-in-out" />
              ) : (
                <Star className="mr-2 h-5 w-5 transition-transform duration-300 ease-in-out" />
              )
            ) : (
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            )}
            {fetcher.state === "idle"
              ? isFavorite
                ? "Remover dos favoritos"
                : "Adicionar aos favoritos"
              : "Salvando..."}
          </Button>
        </fetcher.Form>
      ) : (
        // Logged-out user: Show a link to the login page
        <Button
          variant="ghost"
          className={`${buttonBaseClasses} bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 dark:from-blue-950/40 dark:to-blue-900/30 dark:text-blue-300`}
          size="lg"
          asChild
        >
          <Link
            rel="nofollow"
            to={`/login?redirectTo=${encodeURIComponent(stationPageUrl)}`}
          >
            <Star className="mr-2 h-5 w-5 transition-transform duration-300 ease-in-out" />
            Adicionar aos favoritos
          </Link>
        </Button>
      )}
    </>
  );
}
